import { supabase } from '../lib/supabase';

const projectService = {
  /**
   * Calculate ROI for a project
   */
  calculateROI(project, property) {
    if (!project || !property) {
      return {
        plannedROI: 0,
        actualROI: 0,
        valueIncrease: 0,
        annualRentIncrease: 0
      };
    }

    const estimatedCost = parseFloat(project?.estimated_cost) || 0;
    const actualCost = parseFloat(project?.actual_cost) || 0;
    const valueIncreasePercent = parseFloat(project?.value_increase_percent) || 0;
    const rentIncreasePercent = parseFloat(project?.rent_increase_percent) || 0;
    
    const propertyValue = parseFloat(property?.purchase_price) || 0;
    // Try multiple possible property revenue fields
    const monthlyRent = parseFloat(property?.total_monthly_revenue) || 
                        parseFloat(property?.monthly_revenue) || 
                        (property?.unit_revenues && Array.isArray(property.unit_revenues) 
                          ? property.unit_revenues.reduce((sum, r) => sum + (parseFloat(r) || 0), 0)
                          : 0) || 0;
    
    // Calculate value increase (in dollars)
    const valueIncrease = propertyValue > 0 && valueIncreasePercent > 0 
      ? propertyValue * (valueIncreasePercent / 100) 
      : 0;
    
    // Calculate annual rent increase
    const annualRentIncrease = monthlyRent > 0 && rentIncreasePercent > 0
      ? monthlyRent * 12 * (rentIncreasePercent / 100)
      : 0;
    
    // ROI = (Value Increase) / Cost × 100
    const plannedROI = estimatedCost > 0 && valueIncrease > 0 
      ? (valueIncrease / estimatedCost) * 100 
      : 0;
    const actualROI = actualCost > 0 && valueIncrease > 0 
      ? (valueIncrease / actualCost) * 100 
      : 0;
    
    return {
      plannedROI: isNaN(plannedROI) ? 0 : plannedROI,
      actualROI: isNaN(actualROI) ? 0 : actualROI,
      valueIncrease: isNaN(valueIncrease) ? 0 : valueIncrease,
      annualRentIncrease: isNaN(annualRentIncrease) ? 0 : annualRentIncrease
    };
  },

  /**
   * Get all projects for a property
   */
  async getAll(propertyId) {
    try {
      const { data, error } = await supabase
        .from('property_projects')
        .select('*')
        .eq('property_id', propertyId)
        .order('planned_start_date', { ascending: true });

      if (error) throw error;

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error fetching projects:', error);
      return { success: false, error: error?.message || 'Erreur lors du chargement des projets' };
    }
  },

  /**
   * Get a single project by ID
   */
  async getById(projectId) {
    try {
      const { data, error } = await supabase
        .from('property_projects')
        .select('*')
        .eq('id', projectId)
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error fetching project:', error);
      return { success: false, error: error?.message || 'Erreur lors du chargement du projet' };
    }
  },

  /**
   * Create a new project
   */
  async create(projectData, propertyId, userId) {
    try {
      const { data, error } = await supabase
        .from('property_projects')
        .insert({
          property_id: propertyId,
          user_id: userId,
          name: projectData?.name || '',
          description: projectData?.description || '',
          project_type: projectData?.project_type || 'other',
          estimated_cost: parseFloat(projectData?.estimated_cost) || 0,
          actual_cost: projectData?.actual_cost ? parseFloat(projectData.actual_cost) : null,
          value_increase_percent: projectData?.value_increase_percent ? parseFloat(projectData.value_increase_percent) : null,
          rent_increase_percent: projectData?.rent_increase_percent ? parseFloat(projectData.rent_increase_percent) : null,
          planned_start_date: projectData?.planned_start_date || null,
          planned_end_date: projectData?.planned_end_date || null,
          actual_start_date: projectData?.actual_start_date || null,
          actual_end_date: projectData?.actual_end_date || null,
          status: projectData?.status || 'planned',
          completion_notes: projectData?.completion_notes || ''
        })
        .select()
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error creating project:', error);
      return { success: false, error: error?.message || 'Erreur lors de la création du projet' };
    }
  },

  /**
   * Update an existing project
   */
  async update(projectId, projectData, propertyId, userId) {
    try {
      const { data, error } = await supabase
        .from('property_projects')
        .update({
          name: projectData?.name || '',
          description: projectData?.description || '',
          project_type: projectData?.project_type || 'other',
          estimated_cost: parseFloat(projectData?.estimated_cost) || 0,
          actual_cost: projectData?.actual_cost ? parseFloat(projectData.actual_cost) : null,
          value_increase_percent: projectData?.value_increase_percent ? parseFloat(projectData.value_increase_percent) : null,
          rent_increase_percent: projectData?.rent_increase_percent ? parseFloat(projectData.rent_increase_percent) : null,
          planned_start_date: projectData?.planned_start_date || null,
          planned_end_date: projectData?.planned_end_date || null,
          actual_start_date: projectData?.actual_start_date || null,
          actual_end_date: projectData?.actual_end_date || null,
          status: projectData?.status || 'planned',
          completion_notes: projectData?.completion_notes || ''
        })
        .eq('id', projectId)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error updating project:', error);
      return { success: false, error: error?.message || 'Erreur lors de la mise à jour du projet' };
    }
  },

  /**
   * Delete a project
   */
  async delete(projectId, userId) {
    try {
      const { error } = await supabase
        .from('property_projects')
        .delete()
        .eq('id', projectId)
        .eq('user_id', userId);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error deleting project:', error);
      return { success: false, error: error?.message || 'Erreur lors de la suppression du projet' };
    }
  }
};

export default projectService;
